// ============================================
// RequestReviewModal.jsx - REQUEST REVIEW WIZARD
// Collects reader type / coins / deadline, then calls the
// server (/request-review) which does ALL coin deduction and
// book-copy creation. This component never writes coins or
// creates a book itself.
// ============================================

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { requestReviewServer } from './serverApi';

const STEP_LABELS = ['Choose reader', 'Coins to pay', 'Deadline', 'Review & submit'];
const TOTAL_STEPS = STEP_LABELS.length;

const READER_TYPES = [
  {
    value: 'standard',
    label: 'Standard',
    color: 'blue',
    description: 'All readers can review using a free copy you provide.'
  },
  {
    value: 'kindle',
    label: 'Kindle',
    color: 'orange',
    description: 'Readers with Kindle Unlimited can review your book.'
  },
  {
    value: 'verified',
    label: 'Verified Purchaser',
    color: 'green',
    description: 'Readers who purchase your book on Amazon can review it.'
  }
];

const COLOR_CLASSES = {
  blue: { active: 'border-blue-500 bg-blue-50', text: 'text-blue-600' },
  orange: { active: 'border-orange-500 bg-orange-50', text: 'text-orange-600' },
  green: { active: 'border-green-500 bg-green-50', text: 'text-green-600' }
};

const minCoinsFor = (readerType) => (readerType === 'verified' ? 20 : 10);

const RequestReviewModal = ({ book, currentUser, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [readerType, setReaderType] = useState('');
  const [coins, setCoins] = useState('');
  const [days, setDays] = useState(7);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const coinsNum = parseInt(coins, 10) || 0;
  const urgentFee = readerType !== 'verified' && days < 7 ? 3 : 0;
  const total = coinsNum + urgentFee;

  const goBack = () => {
    setError('');
    setStep(prev => Math.max(1, prev - 1));
  };

  const goNext = () => {
    setError('');

    if (step === 1) {
      if (!readerType) {
        setError('Please choose a reader type.');
        return;
      }
      // Pre-fill the coin input with this reader type's minimum the first time.
      setCoins(prev => (prev ? prev : String(minCoinsFor(readerType))));
      setStep(2);
      return;
    }

    if (step === 2) {
      const min = minCoinsFor(readerType);
      if (!coinsNum || coinsNum < min) {
        setError(`Minimum is ${min} coins for ${readerType === 'verified' ? 'Verified Purchaser' : 'this reader type'}.`);
        return;
      }
      if ((currentUser?.coins || 0) < coinsNum) {
        setError('Not enough coins — top up by buying coins or earning through reviews');
        return;
      }
      setStep(3);
      return;
    }

    if (step === 3) {
      setStep(4);
      return;
    }
  };

  const handleSubmit = async () => {
    if ((currentUser?.coins || 0) < total) return;

    setSubmitting(true);
    setError('');

    const result = await requestReviewServer(book.id, readerType, coinsNum, days);

    setSubmitting(false);

    if (!result.success) {
      setError(result.error || 'Request failed');
      return;
    }

    alert('✅ Review requested! Your book is now live in Browse.');
    onSuccess(result.newBalance);
    onClose();
  };

  const readerLabel = READER_TYPES.find(r => r.value === readerType)?.label || readerType;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <div>
            <h2 className="text-lg font-bold">Request Review</h2>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{book?.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-4 sm:px-6 pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {step} of {TOTAL_STEPS}: {STEP_LABELS[step - 1]}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* STEP 1: Choose reader */}
          {step === 1 && (
            <div className="space-y-3">
              {READER_TYPES.map(rt => {
                const colors = COLOR_CLASSES[rt.color];
                const active = readerType === rt.value;
                return (
                  <label
                    key={rt.value}
                    className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition ${
                      active ? colors.active : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="readerType"
                      checked={active}
                      onChange={() => setReaderType(rt.value)}
                      className="w-5 h-5 mt-1"
                    />
                    <div className="flex-1">
                      <div className={`font-semibold mb-1 ${active ? colors.text : 'text-gray-800'}`}>
                        {rt.label}
                      </div>
                      <p className="text-sm text-gray-600">{rt.description}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {/* STEP 2: Coins to pay */}
          {step === 2 && (
            <div>
              <label className="block text-sm font-medium mb-1">Coins to pay reviewer</label>
              <input
                type="number"
                min={minCoinsFor(readerType)}
                value={coins}
                onChange={(e) => setCoins(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-lg"
                placeholder={`e.g. ${minCoinsFor(readerType)}`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum: {minCoinsFor(readerType)} coins {readerType === 'verified' ? '(Verified Purchaser)' : ''}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Your balance: {currentUser?.coins ?? 0} coins
              </p>
            </div>
          )}

          {/* STEP 3: Deadline */}
          {step === 3 && (
            <div>
              <label className="block text-sm font-medium mb-1">Deadline (days)</label>
              <input
                type="range"
                min={2}
                max={30}
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10))}
                className="w-full"
              />
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="number"
                  min={2}
                  max={30}
                  value={days}
                  onChange={(e) => setDays(Math.max(2, Math.min(30, parseInt(e.target.value, 10) || 2)))}
                  className="w-24 px-3 py-2 border rounded-lg"
                />
                <span className="text-sm text-gray-600">days</span>
              </div>

              <p className="text-xs text-gray-500 mt-3">
                {readerType === 'verified'
                  ? '💡 Verified Purchaser: any 2–30 days, no extra charge.'
                  : '💡 7–30 days: no extra charge. Under 7 days is urgent: +3 coins.'}
              </p>

              <div className="mt-4 bg-indigo-50 p-3 rounded-lg text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Review payment:</span>
                  <span className="font-medium">{coinsNum} coins</span>
                </div>
                {urgentFee > 0 && (
                  <div className="flex justify-between text-orange-700">
                    <span>Urgent fee:</span>
                    <span className="font-medium">{urgentFee} coins</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-indigo-200 pt-1 font-bold">
                  <span>Total:</span>
                  <span>{total} coins</span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Review & Submit */}
          {step === 4 && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Reader type</span>
                  <span className="font-medium">{readerLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Coins to pay reviewer</span>
                  <span className="font-medium">{coinsNum}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Deadline</span>
                  <span className="font-medium">{days} days{urgentFee > 0 ? ' (urgent)' : ''}</span>
                </div>
                {urgentFee > 0 && (
                  <div className="flex justify-between text-orange-700">
                    <span>Urgent fee</span>
                    <span className="font-medium">+{urgentFee}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 font-bold text-base">
                  <span>Total</span>
                  <span>{total} coins</span>
                </div>
              </div>

              {(currentUser?.coins || 0) < total && (
                <p className="text-sm text-red-600 font-semibold">
                  Insufficient balance, top up to submit
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {error}
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-t">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 1 || submitting}
            className="px-5 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Back
          </button>

          {step < TOTAL_STEPS ? (
            <button
              type="button"
              onClick={goNext}
              className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 font-semibold"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || (currentUser?.coins || 0) < total}
              className="flex-1 bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                'SUBMIT'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestReviewModal;
